import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { Button, Icon, Input, useToast } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import "./change-password.css";

/**
 * Choosing your own password.
 *
 * Serves two situations. Normally it is a voluntary change and asks for the
 * current password, so a borrowed session cannot be turned into a permanent
 * takeover. When an administrator has issued a temporary one it becomes
 * mandatory instead: the account is unusable until the password is replaced,
 * because a credential someone else chose is a shared secret rather than a
 * password.
 */

const MIN_PASSWORD = 12;

export function ChangePassword() {
  usePageTitle("Change password");
  const navigate = useNavigate();
  const { toast } = useToast();
  const { viewer, refresh, signOut } = useAuth();

  const forced = viewer?.mustChangePassword === true;

  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const mismatch = confirm.length > 0 && password !== confirm;
  const tooShort = password.length > 0 && password.length < MIN_PASSWORD;
  const canSubmit =
    password.length >= MIN_PASSWORD && password === confirm && (forced || current.length > 0) && !saving;

  const submit = useCallback(() => {
    if (!canSubmit) return;
    setSaving(true);
    api
      .post("/auth/change-password", {
        // Omitted on a forced change: the current password is the one the
        // administrator just handed over, so asking for it proves nothing.
        currentPassword: forced ? undefined : current,
        password,
      })
      .then(async () => {
        await refresh();
        toast("Password changed.");
        navigate(forced ? "/home" : "/profile", { replace: true });
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not change your password.", { tone: "danger" });
        setSaving(false);
      });
  }, [canSubmit, forced, current, password, refresh, navigate, toast]);

  return (
    <div className={forced ? "change-password change-password--forced" : "change-password"}>
      <form
        className="change-password__card"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {forced && (
          <div className="change-password__brand">
            <span className="change-password__mark">A</span>
            <span className="change-password__wordmark">
              ASTRUM<strong>CONNECT</strong>
            </span>
          </div>
        )}

        <h1>{forced ? "Choose your own password" : "Change your password"}</h1>

        <p className="change-password__muted">
          {forced
            ? "An administrator set a temporary password for you. Pick one only you know before carrying on."
            : "You will stay signed in here. Every other device will be signed out."}
        </p>

        {!forced && (
          <Input
            label="Current password"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoFocus
            required
          />
        )}

        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint={`At least ${MIN_PASSWORD} characters. A memorable phrase beats a short, complicated one.`}
          error={tooShort ? `Use at least ${MIN_PASSWORD} characters.` : undefined}
          autoFocus={forced}
          required
        />

        <Input
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={mismatch ? "These do not match." : undefined}
          required
        />

        <div className="change-password__actions">
          {/* No "cancel" on a forced change — there is nowhere to go. Signing
              out is the honest alternative for someone who would rather not
              continue. */}
          {forced ? (
            <Button type="button" variant="ghost" onClick={() => void signOut()}>
              Sign out
            </Button>
          ) : (
            <Button type="button" variant="ghost" onClick={() => navigate("/profile")}>
              Cancel
            </Button>
          )}
          <Button type="submit" loading={saving} disabled={!canSubmit}>
            {forced ? "Set password" : "Change password"}
          </Button>
        </div>

        {forced && (
          <p className="change-password__note">
            <Icon name="lock" size={12} />
            Your access and security events are recorded.
          </p>
        )}
      </form>
    </div>
  );
}
